/**
 * Докачиваемая multipart-загрузка прямо в объектное хранилище.
 *
 * Принципы:
 *  - файл никогда не читается целиком: части нарезаются через File.slice(),
 *    а браузер стримит Blob сам;
 *  - три части грузятся параллельно, каждая с тремя попытками и растущей
 *    паузой между ними;
 *  - прогресс считается по XMLHttpRequest.upload.onprogress, потому что fetch
 *    прогресса отдачи не даёт;
 *  - состояние {assetId, uploadId, завершённые части} лежит в localStorage,
 *    поэтому после выгрузки WebView из памяти (обычное дело на iOS) загрузка
 *    продолжается с места обрыва, а не с нуля;
 *  - ETag каждой части забираем из заголовка ответа и отдаём в
 *    POST /assets/:id/complete.
 */
import type { AssetKind } from '@reels/shared';
import { ApiError, api, isDemo } from './api';
import type { CompletedPart, CreateAssetResponse, ProjectAsset, UploadPartUrl } from './types';
import { contentTypeOf } from './video';
import { t } from '../i18n';

export interface UploadProgress {
  /** Байт отправлено, включая незавершённые части. */
  uploaded: number;
  total: number;
  /** 0..1 */
  ratio: number;
  partsDone: number;
  partsTotal: number;
  /** true, если загрузка продолжена после обрыва. */
  resumed: boolean;
}

export type UploadPhase = 'preparing' | 'uploading' | 'completing' | 'done';

export interface UploadOptions {
  position?: number;
  onRegistered?: (assetId: string) => void;
  projectId: string;
  file: File;
  kind: AssetKind;
  durationS?: number | null;
  onProgress?: (progress: UploadProgress) => void;
  onPhase?: (phase: UploadPhase) => void;
  signal?: AbortSignal;
}

export interface UploadResult {
  assetId: string;
  asset: ProjectAsset;
  resumed: boolean;
}

interface PersistedUpload {
  fingerprint: string;
  projectId: string;
  assetId: string;
  uploadId: string;
  key: string;
  partSizeBytes: number;
  parts: UploadPartUrl[];
  completed: CompletedPart[];
  initAt: number;
}

const STORAGE_PREFIX = 'reels.upload.v1.';
/** Подписанные ссылки живут час, берём запас. */
const PRESIGN_TTL_MS = 50 * 60 * 1000;
const CONCURRENCY = 3;
const PART_ATTEMPTS = 3;
const RETRY_BASE_MS = 700;

export class UploadAbortedError extends Error {
  constructor() {
    super(t('upload.aborted'));
    this.name = 'UploadAbortedError';
  }
}

class PresignExpiredError extends Error {
  constructor() {
    super(t('upload.links_expired'));
    this.name = 'PresignExpiredError';
  }
}

/** Хранилище не знает такой загрузки: сохранённая сессия устарела (сервер откатили или очистили). */
class StaleUploadError extends Error {
  constructor() {
    super(t('upload.stale'));
    this.name = 'StaleUploadError';
  }
}

export function fingerprintOf(projectId: string, file: File): string {
  return `${projectId}:${file.name}:${file.size}:${file.lastModified}`;
}

function storageKey(fingerprint: string): string {
  return `${STORAGE_PREFIX}${fingerprint}`;
}

function readStorage(fingerprint: string): PersistedUpload | null {
  try {
    const raw = window.localStorage.getItem(storageKey(fingerprint));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedUpload;
    if (parsed.fingerprint !== fingerprint) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(session: PersistedUpload): void {
  try {
    window.localStorage.setItem(storageKey(session.fingerprint), JSON.stringify(session));
  } catch {
    // Приватный режим или переполнение — докачка просто не сработает.
  }
}

function dropStorage(fingerprint: string): void {
  try {
    window.localStorage.removeItem(storageKey(fingerprint));
  } catch {
    /* см. выше */
  }
}

/** Есть ли незаконченная загрузка этого файла, которую можно продолжить. */
export function resumableState(projectId: string, file: File): PersistedUpload | null {
  const session = readStorage(fingerprintOf(projectId, file));
  if (!session) return null;
  return session;
}

/** Уборка записей, которые уже не пригодятся. */
export function cleanupStaleUploads(): void {
  try {
    const now = Date.now();
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as PersistedUpload;
        if (now - parsed.initAt > PRESIGN_TTL_MS) window.localStorage.removeItem(key);
      } catch {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    /* localStorage недоступен */
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError());
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      window.clearTimeout(timer);
      reject(new UploadAbortedError());
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

interface PartPlan {
  partNumber: number;
  url: string;
  start: number;
  end: number;
}

function planParts(file: File, session: PersistedUpload): PartPlan[] {
  const size = session.partSizeBytes > 0 ? session.partSizeBytes : file.size;
  return session.parts.map((part) => {
    const start = (part.partNumber - 1) * size;
    const end = Math.min(start + size, file.size);
    return { partNumber: part.partNumber, url: part.url, start, end };
  });
}

/**
 * Отправка одной части. XMLHttpRequest выбран ради upload.onprogress,
 * которого нет у fetch.
 */
function putPart(
  plan: PartPlan,
  file: File,
  onLoaded: (loaded: number) => void,
  signal: AbortSignal | undefined,
  track: (xhr: XMLHttpRequest) => () => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError());
      return;
    }
    const blob = file.slice(plan.start, plan.end);
    const xhr = new XMLHttpRequest();
    const untrack = track(xhr);
    let aborted = false;

    const onAbort = (): void => {
      aborted = true;
      xhr.abort();
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    const cleanup = (): void => {
      untrack();
      signal?.removeEventListener('abort', onAbort);
    };

    xhr.open('PUT', plan.url, true);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onLoaded(event.loaded);
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status === 403 || xhr.status === 401 || xhr.status === 400) {
        reject(new PresignExpiredError());
        return;
      }
      if (xhr.status === 404) {
        reject(new StaleUploadError());
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(t('upload.error.storage_status', { status: xhr.status })));
        return;
      }
      const etag = xhr.getResponseHeader('ETag') ?? xhr.getResponseHeader('etag');
      if (!etag) {
        reject(new Error(t('upload.error.no_etag')));
        return;
      }
      onLoaded(plan.end - plan.start);
      resolve(etag);
    };
    xhr.onerror = () => {
      cleanup();
      reject(aborted ? new UploadAbortedError() : new Error(t('upload.error.part_dropped')));
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(new Error(t('upload.error.part_timeout')));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new UploadAbortedError());
    };
    xhr.send(blob);
  });
}

async function initSession(options: UploadOptions, fingerprint: string): Promise<PersistedUpload> {
  const response: CreateAssetResponse = await api.createAsset(options.projectId, {
    kind: options.kind,
    position: options.position,
    filename: options.file.name,
    bytes: options.file.size,
    contentType: contentTypeOf(options.file),
    ...(typeof options.durationS === 'number' ? { durationS: options.durationS } : {}),
  });
  const session: PersistedUpload = {
    fingerprint,
    projectId: options.projectId,
    assetId: response.assetId,
    uploadId: response.uploadId,
    key: response.key,
    partSizeBytes: response.partSizeBytes,
    parts: response.parts,
    completed: [],
    initAt: Date.now(),
  };
  writeStorage(session);
  return session;
}

/**
 * Главная функция. Возвращает завершённый ассет; при обрыве бросает ошибку,
 * но запись о прогрессе остаётся — повторный вызов продолжит с той же точки.
 */
export async function uploadAsset(options: UploadOptions): Promise<UploadResult> {
  if (isDemo()) return demoUpload(options);

  const { file, signal } = options;
  const fingerprint = fingerprintOf(options.projectId, file);
  options.onPhase?.('preparing');

  const stored = resumableState(options.projectId, file);
  let session = stored ?? (await initSession(options, fingerprint));
  options.onRegistered?.(session.assetId);
  const refresh = async (): Promise<ProjectAsset | undefined> => {
    const response = await api.refreshUpload(session.assetId);
    if (response.complete && response.asset) { dropStorage(fingerprint); return response.asset; }
    if (!response.parts?.length) throw new Error(t('upload.error.refresh_links'));
    session = { ...session, parts: response.parts, initAt: Date.now() };
    writeStorage(session);
    return undefined;
  };
  if (stored && Date.now() - stored.initAt > PRESIGN_TTL_MS) {
    const complete = await refresh();
    if (complete) return { assetId: session.assetId, asset: complete, resumed: true };
  }
  if (signal?.aborted) { await api.deleteAsset(session.assetId); dropStorage(fingerprint); throw new UploadAbortedError(); }
  const resumed = stored !== null && stored.completed.length > 0;

  let restarted = false;

  const run = async (): Promise<CompletedPart[]> => {
    const plans = planParts(file, session);
    const doneNumbers = new Set(session.completed.map((part) => part.partNumber));
    const pending = plans.filter((plan) => !doneNumbers.has(plan.partNumber));
    const inFlight = new Map<number, number>();
    const activeRequests = new Set<XMLHttpRequest>();

    const completedBytes = (): number =>
      plans
        .filter((plan) => doneNumbers.has(plan.partNumber))
        .reduce((sum, plan) => sum + (plan.end - plan.start), 0);

    const report = (): void => {
      let uploaded = completedBytes();
      for (const loaded of inFlight.values()) uploaded += loaded;
      const capped = Math.min(uploaded, file.size);
      options.onProgress?.({
        uploaded: capped,
        total: file.size,
        ratio: file.size > 0 ? capped / file.size : 1,
        partsDone: doneNumbers.size,
        partsTotal: plans.length,
        resumed,
      });
    };

    options.onPhase?.('uploading');
    report();

    let cursor = 0;
    const worker = async (): Promise<void> => {
      for (;;) {
        if (signal?.aborted) throw new UploadAbortedError();
        const index = cursor;
        cursor += 1;
        const plan = pending[index];
        if (!plan) return;

        let lastError: unknown = null;
        for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt += 1) {
          try {
            const etag = await putPart(
              plan,
              file,
              (loaded) => {
                inFlight.set(plan.partNumber, loaded);
                report();
              },
              signal,
              (xhr) => {
                activeRequests.add(xhr);
                return () => activeRequests.delete(xhr);
              },
            );
            inFlight.delete(plan.partNumber);
            doneNumbers.add(plan.partNumber);
            session.completed = [
              ...session.completed.filter((part) => part.partNumber !== plan.partNumber),
              { partNumber: plan.partNumber, etag },
            ];
            writeStorage(session);
            report();
            lastError = null;
            break;
          } catch (error) {
            inFlight.delete(plan.partNumber);
            report();
            if (error instanceof UploadAbortedError) throw error;
            if (error instanceof PresignExpiredError || error instanceof StaleUploadError) throw error;
            lastError = error;
            if (attempt < PART_ATTEMPTS) {
              // 0.7 с, 1.4 с, 2.8 с плюс случайная добавка против совпадения повторов.
              await sleep(RETRY_BASE_MS * 2 ** (attempt - 1) + Math.random() * 250, signal);
            }
          }
        }
        if (lastError) {
          throw lastError instanceof Error
            ? lastError
            : new Error(t('upload.error.part_failed'));
        }
      }
    };

    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, Math.max(pending.length, 1)) }, worker));
    } catch (error) {
      for (const xhr of activeRequests) xhr.abort();
      activeRequests.clear();
      throw error;
    }

    return [...session.completed].sort((left, right) => left.partNumber - right.partNumber);
  };

  let parts: CompletedPart[];
  try {
    parts = await run();
  } catch (error) {
    if (error instanceof PresignExpiredError && !restarted) {
      // Ссылки протухли: начинаем загрузку заново с новыми подписями.
      restarted = true;
      const complete = await refresh();
      if (complete) return { assetId: session.assetId, asset: complete, resumed: true };
      parts = await run();
    } else if (error instanceof StaleUploadError && stored !== null && !restarted) {
      // Запись о загрузке пропала на сервере: забываем её и грузим файл с нуля.
      restarted = true;
      dropStorage(fingerprint);
      session = await initSession(options, fingerprint);
      options.onRegistered?.(session.assetId);
      parts = await run();
    } else {
      throw error;
    }
  }

  options.onPhase?.('completing');
  const response = await api.completeAsset(session.assetId, parts);
  dropStorage(fingerprint);
  options.onPhase?.('done');
  return { assetId: session.assetId, asset: response.asset, resumed };
}

/** Демо-режим: имитируем загрузку, чтобы экраны можно было пройти в браузере. */
async function demoUpload(options: UploadOptions): Promise<UploadResult> {
  const total = options.file.size;
  const registered = await api.createAsset(options.projectId, { kind: options.kind, filename: options.file.name, bytes: total, contentType: contentTypeOf(options.file), durationS: options.durationS ?? undefined, position: options.position });
  options.onRegistered?.(registered.assetId);
  options.onPhase?.('preparing');
  const steps = 20;
  for (let step = 1; step <= steps; step += 1) {
    if (options.signal?.aborted) throw new UploadAbortedError();
    await sleep(90, options.signal);
    options.onPhase?.('uploading');
    const uploaded = Math.round((total * step) / steps);
    options.onProgress?.({
      uploaded,
      total,
      ratio: step / steps,
      partsDone: step,
      partsTotal: steps,
      resumed: false,
    });
  }
  options.onPhase?.('completing');
  const assetId = registered.assetId;
  const complete = await api.completeAsset(assetId, [{ partNumber: 1, etag: 'demo' }]);
  options.onPhase?.('done');
  return {
    assetId,
    asset: complete.asset,
    resumed: false,
  };
}

export function uploadErrorMessage(error: unknown): string {
  if (error instanceof UploadAbortedError) return t('upload.aborted');
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message.length > 0) return error.message;
  return t('upload.error.generic');
}
